import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { fetchProperties } from 'entities/Property/model/services/fetchProperties/fetchProperties';
import { propertyActions } from 'entities/Property/model/slices/PropertySlice';
import {
    getRouteCatalog,
    getRouteForbidden,
    getRoutePropertiesItem,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { isArray } from 'shared/lib/lodash/lodash';

import { Property } from '../types/PropertySchema';

export const useFetchProperties = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const selectedPropertyId = useSelector(getSelectedPropertyId);

    const initialPropertyIdParam = useRef(null);
    const navigateRef = useRef(null);
    const pathnameRef = useRef(location.pathname);
    const paramsPropertyIdRef = useRef(params.propertyId);
    const selectedPropertyIdRef = useRef(selectedPropertyId);
    selectedPropertyIdRef.current = selectedPropertyId;
    paramsPropertyIdRef.current = params.propertyId;
    pathnameRef.current = location.pathname;

    useInitialEffect(() => {
        initialPropertyIdParam.current = params.propertyId;
        navigateRef.current = navigate;
        pathnameRef.current = location.pathname;
    });

    const onFetchProperties = useCallback(
        async (isOrdersPage?: boolean) => {
            // Prefer live URL param; ignore corrupt `/catalog/undefined` leftovers.
            const rawPropId =
                paramsPropertyIdRef.current || initialPropertyIdParam.current;
            const propId =
                rawPropId && rawPropId !== 'undefined' ? rawPropId : null;
            const navigate = navigateRef.current;
            if (propId) {
                dispatch(propertyActions.setPropertyId(propId));
            }

            const response = await dispatch(fetchProperties());

            if (
                propId &&
                isArray(response.payload) &&
                !response.payload.find((prop: Property) => prop.id === propId)
            ) {
                navigate(getRouteForbidden());
                return null;
            }

            if (
                !isOrdersPage &&
                !propId &&
                isArray(response.payload) &&
                response.payload.length > 0
            ) {
                const currentSelectedId = selectedPropertyIdRef.current;
                const isSelectedExists =
                    currentSelectedId &&
                    response.payload.some(
                        (prop: Property) => prop.id === currentSelectedId,
                    );
                const targetId = isSelectedExists
                    ? currentSelectedId
                    : response.payload[0].id;
                const isCatalogSection =
                    pathnameRef.current === '/catalog' ||
                    pathnameRef.current.startsWith('/catalog/');

                navigate(
                    isCatalogSection
                        ? getRouteCatalog(targetId)
                        : getRoutePropertiesItem(targetId),
                );
            }

            return response;
        },
        [dispatch],
    );

    return onFetchProperties;
};
